import React, { useEffect } from 'react';
import axios from 'axios';
import { Container, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { captureException, addBreadcrumb, Severity } from '@sentry/react';
import { useUpdateSettingPreferencesForCollectorSetting } from '../hooks/useUpdateSettingPreferencesForCollectorSetting';
import { CollectorIntermediatePageSkeleton } from './CollectorIntermediatePageSkeleton';
import { InstallBanner } from './InstallBanner';
import {
  useInstallCollector,
  collectorBroadcast,
  useCollectorItem
} from './hooks';
import useCollectorIntermediateStyles from './hooks/useCollectorIntermediateStyles';

const refreshDesktopCollectors = async (refreshURL: string) => {
  try {
    await axios.get(refreshURL);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { ecid } = error.response.headers;
      addBreadcrumb({
        type: 'http',
        category: 'refreshDesktopCollectors',
        data: { ecid: ecid, status: error.response.status },
        level: Severity.Info
      });
    }
    captureException(error);
  }
};

export const CollectorSuccess: React.FC = () => {
  const classes = useCollectorIntermediateStyles();
  const {
    collectorId,
    collectorItem,
    scope,
    loading,
    syncRequired,
    noAction,
    collectorMessage
  } = useCollectorItem();
  const { installCollector } = useInstallCollector();
  const refreshURL = localStorage.getItem('collectorDesktopRefreshURL');
  const {
    updateSettingPreferences
  } = useUpdateSettingPreferencesForCollectorSetting();
  useEffect(() => {
    const saveStatus = async () => {
      if (collectorId && !noAction) {
        try {
          const dataSet = await installCollector(collectorId, scope);
          if (syncRequired) {
            await updateSettingPreferences({
              installId: dataSet?.data?.installCollector?.installId,
              settings: [
                {
                  key: 'syncStatus',
                  value: 'InProgress'
                }
              ]
            });
          }
        } catch (e) {
          const msg = (e as Error).message;
          if (msg === 'Collector is already installed') {
            console.info(msg);
          } else {
            console.error(e);
            throw e;
          }
        }
        if (refreshURL) {
          await refreshDesktopCollectors(refreshURL);
          localStorage.removeItem('collectorDesktopRefreshURL');
        } else {
          await collectorBroadcast.postMessage(collectorId);
        }
      }
    };
    saveStatus();
  }, [
    collectorId,
    installCollector,
    scope,
    refreshURL,
    syncRequired,
    updateSettingPreferences,
    noAction
  ]);

  if (loading) return <CollectorIntermediatePageSkeleton />;
  return (
    <div className={classes.root} data-testid="collector-success-page">
      <Container className={classes.textContainer}>
        <Typography variant="h2">
          <FormattedMessage id="collectors.installSuccessMessage1" />
        </Typography>
      </Container>
      <InstallBanner imageSrc={collectorItem?.imageSrc} classes={classes} />
      <Container className={classes.textContainer}>
        {syncRequired && (
          <div data-testid="collector-syncMessage">
            <Typography align="center" variant="body1">
              <FormattedMessage
                id={`collectors.syncMessages.${collectorId}`}
                values={{ collector: collectorItem?.name }}
              />
            </Typography>
          </div>
        )}
        <Typography variant="h4">
          <FormattedMessage
            id="collectors.installSuccessMessage2"
            values={{ collector: collectorItem?.name }}
          />
        </Typography>
        {collectorMessage && (
          <Typography data-testid="collector-message" variant="h4">
            {collectorMessage}
          </Typography>
        )}
        <Typography variant="h4">
          <FormattedMessage id="collectors.installSuccessMessage3" />
        </Typography>
      </Container>
    </div>
  );
};

export default React.memo(CollectorSuccess);
